import styled from '@emotion/styled';
import { Flex, Heading, Image } from '@rebass/emotion';
import { navigate } from 'gatsby';
import Text from '~components/Elements/Text';
import Root from '~components/Root';
import Footer from './Footer';
import React, { useEffect } from 'react';
import Header from './Header/index';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useMobileScreen } from '~lib/responsive';

import pathwayAdultsImage from '~images/pathway-adults.jpg';
import pathwayChildrenImage from '~images/pathway-children.jpg';

const App = styled(Flex)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  padding: 0%;
  max-width: 100vw;
  font-family: ${props => props.theme.fonts.sans};
`;

const Box = styled(Flex)`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: ${props => props.theme.fonts.sans};
`;

const PlayerWrapper = styled(Flex)`
  align-self: center;
  position: relative;
  overflow: hidden;
  width: 80%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
  border-radius: 20px;
  z-index: -10;
`;

const HeroColourBlock = styled(Flex)`
  flex-direction: column;
  align-items: center;
  width: auto;

  flex-grow: 0;
  background-image: radial-gradient(
      circle at 0 0,
      ${props => props.theme.colors.blueBlue20},
      rgba(255, 255, 255, 0)
    ),
    linear-gradient(
      to top,
      ${props => props.theme.colors.blueBlue20},
      ${props => props.theme.colors.blueBlue20},
      ${props => props.theme.colors.blueBlue40}
    );

  @media (min-width: ${props => props.theme.device.xl}) {
    width: 969px;
  }
`;

const HeroBoxColumn = styled(Flex)`
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 4vh 4vw;
  /* width: 60%; */
  gap: 2vh;
  text-align: center;
  align-self: center;
`;

const H1 = styled(Heading)`
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  background-image: linear-gradient(
    to top,
    ${props => props.theme.colors.blueBlue160},
    ${props => props.theme.colors.blueBlue120}
  );
`;

const StageBox = ({
  selected,
  afterSelected,
  index,
  title,
  description,
  to,
}) => {
  const isMobile = useMobileScreen();

  const StageUnselected = styled(Flex)`
    /* height: 172px; */
    justify-content: space-between;
    width: 100%;
    max-width: 604px;
    background-color: ${({ theme }) => theme.colors.neutralNeutral0};
    border-radius: 8px;
    align-self: center;
    padding: 32px;

    @media (max-width: ${props => props.theme.device.md}) {
      width: 80%;
      padding-right: 16px;
      padding-left: 16px;
    }
  `;

  const StageSelected = styled(Flex)`
    /* height: 256px; */
    width: 100%;
    max-width: 816px;
    background-color: ${({ theme }) => theme.colors.neutralNeutral0};
    border-radius: 8px;
    box-shadow: 0 24px 48px 0 rgba(9, 30, 66, 0.15),
      0 0 12px 0 rgba(9, 30, 66, 0.05);
    align-self: center;
    padding: 32px;

    @media (max-width: ${props => props.theme.device.md}) {
      width: 100%;
      padding-right: 16px;
      padding-left: 16px;
    }
  `;

  // First Column and content
  const StageArtColumn = styled(Flex)`
    flex-direction: column;
    justify-content: start;
    width: 48px;
    height: 48px;
    margin-top: -6px;
    margin-right: 10px;
  `;

  const Line = styled(Box)`
    align-self: center;
    border-style: solid;
    border-width: 1px;
    margin: 1vh 1vw;
    background-color: ${({ theme }) => theme.colors.neutralNeutral60};
  `;

  const RoundIcon = styled(Box)`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    align-content: start;
    align-self: center;
    border-style: solid;
    border-width: 3px;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0);
    margin-bottom: 4px;
    font-family: ${props => props.theme.fonts.HeadingM};
    border-color: #0052cc;

    ${({ selected }) =>
      selected &&
      `
    border-color: #f96301;
  `}
    ${({ afterSelected }) =>
      afterSelected &&
      `
    border-color: #b3bac5;
    color: #b3bac5;
  `}


    :before {
      content: '';
      z-index: 1;
      top: -140%;
      position: relative;
      left: 50%;
      border: 1px solid #b3bac5;
      border-width: 0 0 0 1px;
    }

    :after {
      content: '';
      z-index: 1;
      top: 100%;
      position: relative;
      left: 40%;
      border: 1px solid #b3bac5;
      border-width: 0 0 0 1px;
    }
  `;

  // Second Column and content
  const StageContentColumn = styled(Flex)`
    flex-direction: column;
    justify-content: space-around;
    height: 100%;
    width: 90%;
    gap: 16px;

    @media (max-width: ${props => props.theme.device.md}) {
      gap: 12px;
    }
  `;

  const StageTitle = styled(Flex)`
    align-items: center;
    color: ${({ theme }) => theme.colors.neutralNeutral180};
    line-height: 32px;
    text-align: left;
    font-size: 20px;
    font-weight: 600;
    padding-left: 10px;

    @media (max-width: ${props => props.theme.device.md}) {
      font-size: 16px;
      line-height: 32px;
    }
  `;

  const StageDesc = styled(Flex)`
    color: ${({ theme }) => theme.colors.neutralNeutral180};
    line-height: 1.5;
    text-align: left;
    font-size: 16px;
    font-weight: 400;
    padding-left: 10px;

    @media (max-width: ${props => props.theme.device.sm}) {
      display: ${props => (props.selected ? 'flex' : 'none')};
    }
  `;

  const BeginHereButton = styled.button`
    height: 40px;
    width: 138px;
    background-color: ${props => props.theme.colors.orangeOrange120};
    font-style: ${props => props.theme.fonts.BodySmallCaps};
    border-radius: 24px;
    border: 0;
    font-size: 16px;
    font-weight: 500;
    margin-top: 10px;
  `;

  return selected ? (
    <StageSelected>
      <StageArtColumn>
        <RoundIcon selected={selected}>{index}</RoundIcon>
      </StageArtColumn>
      <StageContentColumn>
        <StageTitle>{title}</StageTitle>
        <StageDesc selected>{description}</StageDesc>
        <BeginHereButton onClick={() => navigate(to)}>
          Begin here
        </BeginHereButton>
      </StageContentColumn>
    </StageSelected>
  ) : (
    <StageUnselected>
      <StageArtColumn>
        <RoundIcon afterSelected={afterSelected}>{index}</RoundIcon>
      </StageArtColumn>
      <StageContentColumn>
        <StageTitle>{title}</StageTitle>
        {isMobile ? null : <StageDesc>{description}</StageDesc>}
      </StageContentColumn>
    </StageUnselected>
  );
};

const PathwayOverview = ({ pageContext, option }) => {
  const { section, stage } = option;
  const menu = pageContext.pathwayMenus.find(m => m.section === section);

  const [pathwayStage, setPathwayStage] = useLocalStorage(
    'pathway-stage',
    null
  );
  const [pathwaySection, setPathwaySection] = useLocalStorage(
    'pathway-section',
    null
  );

  useEffect(() => {
    setPathwayStage(stage);
    setPathwaySection(section);
  }, []);

  const stages = menu.items.map((item, i) => {
    const step = `${i + 1}`;
    const page = item.items[0].page;
    const to = `/pathway/${section}/${page.topic.slug.current}/${page.slug.current}`;

    return (
      <StageBox
        selected={step === stage}
        afterSelected={step > stage}
        index={step}
        title={item.title}
        description={item.description}
        to={to}
      />
    );
  });

  return (
    <Root pageContext={pageContext}>
      <App>
        <header>
          <Header data={pageContext.header}></Header>
        </header>

        <main>
          <Box>
            <HeroBoxColumn px={['20px', '20px', '30px', '30px', 0]}>
              <H1 as="h2" fontSize="36px" lineHeight="44px">
                {' '}
                Your pathway begins here{' '}
              </H1>
              <Text
                fontSize="20px"
                lineHeight="28px"
                color={'neutralNeutral180'}
              >
                Your autism pathway is designed to present what you need, when
                you need it so you can avoid information overload.
              </Text>
              <Flex
                mx="4vh"
                width="100%"
                maxWidth="969px"
                justifyContent="center"
                alignItems="center"
              >
                <Image
                  src={
                    section === 'adults'
                      ? pathwayAdultsImage
                      : pathwayChildrenImage
                  }
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </Flex>
              {/* <PlayerWrapper>
                <VideoBase
                  url={'https://vimeo.com/54352642'}
                  isIndex={true}
                ></VideoBase>
              </PlayerWrapper> */}
            </HeroBoxColumn>
            <HeroColourBlock px={['10px', '10px', '30px', '30px', 0]}>
              <HeroBoxColumn>
                <Flex
                  flexDirection="column"
                  justifyContent="space-between"
                  alignItems="center"
                  py={[30, 30, 30, 30, 30]}
                >
                  <H1 as="h4" fontSize="28px">
                    How does it work?
                  </H1>
                  <Flex py={'12px'} />
                  <Text
                    fontSize="16px"
                    lineHeight="24px"
                    color={'neutralNeutral180'}
                  >
                    Your pathway is split into steps and stages. Each step
                    builds on the knowledge from the previous step, and each
                    stage is a progression from the last. The steps and stages
                    are checked off as you move along the pathway. While the
                    idea is to keep moving forward, if you feel there is a gap
                    in your knowledge you can go back at any time.
                  </Text>
                </Flex>
                {stages}
              </HeroBoxColumn>
            </HeroColourBlock>
          </Box>
        </main>

        <Footer data={pageContext.footer}></Footer>
      </App>
    </Root>
  );
};

export default PathwayOverview;
